import { type ReactNode, type SelectHTMLAttributes, useId } from "react";
import { cn } from "~/foundation/utils/cn";

/**
 * The few layout parts the git scenes need, kept package-local: a shared package may import the
 * API, the UI kit and itself — never one host's private source (the portability contract).
 * They match slate's automation parts class-for-class, so the surfaces read as one app.
 */
export type StatusTone = "neutral" | "accent" | "success" | "warning" | "danger";

const TONES: Record<StatusTone, string> = {
  neutral: "bg-bg-secondary text-text-secondary",
  accent: "bg-accent/12 text-accent",
  success: "bg-success/16 text-text",
  warning: "bg-warning/18 text-text",
  danger: "bg-danger/14 text-danger",
};

export function Pill({
  children,
  tone = "neutral",
  className,
}: {
  children: ReactNode;
  tone?: StatusTone;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded-sm px-1.5 py-0.5 font-medium text-small",
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

export function Rows({ children }: { children: ReactNode }) {
  return <ul className="flex flex-col rounded-md border border-divider">{children}</ul>;
}

export function Row({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <li
      className={cn(
        "flex items-center gap-3 border-divider border-b px-3 py-2.5 last:border-b-0",
        className,
      )}
    >
      {children}
    </li>
  );
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  fullWidth?: boolean;
}

export function Select({ label, hint, fullWidth, className, id, children, ...rest }: SelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  return (
    <div className={cn("flex flex-col gap-1.5", fullWidth && "w-full")}>
      {label ? (
        <label htmlFor={selectId} className="font-medium text-small text-text-secondary">
          {label}
        </label>
      ) : null}
      <select
        id={selectId}
        className={cn(
          "h-8 rounded-sm border border-input-border bg-bg px-2 text-body text-text",
          "focus:border-input-border-focus focus:outline-none disabled:opacity-50",
          fullWidth && "w-full",
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      {hint ? <span className="text-small text-text-tertiary">{hint}</span> : null}
    </div>
  );
}
